/**
 * PD-16760: vocabulary of the AI usage ledger (`ai_usage_event` and its
 * rollups). See the usage-metering specification, sections 3 and 6.
 */

/** Where a metered charge came from. Stored in `ai_usage_event.surface`. */
export const AI_USAGE_SURFACES = [
	'agent_chat',
	'agent_voice',
	'agent_sms',
	'agent_widget',
	'mcp',
	'api',
	'background',
	// PD-16876: Google Maps Platform requests charged to the Google credit pool.
	'maps',
] as const;

export type AiUsageSurface = (typeof AI_USAGE_SURFACES)[number];

/**
 * What paid for a charge. Every event is `pool` while metering runs in
 * shadow mode (record only); top-ups and overage arrive with enforcement.
 */
export const AI_USAGE_BUCKETS = ['pool', 'topup', 'overage'] as const;

export type AiUsageBucket = (typeof AI_USAGE_BUCKETS)[number];

/**
 * Plain names for each surface, for the usage views (Settings > AI Usage in
 * the CRM and AI Usage in config-web). The screens pass them through t().
 */
export const AI_USAGE_SURFACE_LABELS: Readonly<Record<AiUsageSurface, string>> =
	{
		mcp: 'Claude connector',
		agent_chat: 'In-app assistant chat',
		agent_voice: 'Phone calls',
		agent_sms: 'Text messages',
		agent_widget: 'Website chat',
		api: 'API',
		background: 'Background AI',
		maps: 'Google Maps',
	};

/**
 * The plain name of a surface. A key this build does not know (written by a
 * newer server) is shown as stored rather than hidden.
 */
export const aiUsageSurfaceLabel = (surface: string): string =>
	Object.prototype.hasOwnProperty.call(AI_USAGE_SURFACE_LABELS, surface)
		? AI_USAGE_SURFACE_LABELS[surface as AiUsageSurface]
		: surface;

/**
 * PD-16766: the Claude connector rate limits whose refusals are counted.
 * `user` is per person and client, `tenant` is the whole company, and
 * `heavyConcurrency` is the cap on reports running at the same time.
 */
export const MCP_RATE_LIMIT_KINDS = ['user', 'tenant', 'heavyConcurrency'] as const;

export type McpRateLimitKind = (typeof MCP_RATE_LIMIT_KINDS)[number];

/** Daily refusal counters live about 35 days, so a whole month stays readable. */
export const MCP_RATE_LIMITED_COUNTER_TTL_SECONDS = 35 * 24 * 60 * 60;

/**
 * The daily refusal counter on the shared Redis database (DB 9). crm-api
 * increments it; config-api reads it for the AI Usage page. `day` is the UTC
 * date, `YYYY-MM-DD`.
 */
export const mcpRateLimitedCounterKey = (
	tenantAlias: string,
	kind: McpRateLimitKind,
	day: string,
): string => `tenant:${tenantAlias}:mcp_rate_limited:${kind}:${day}`;
